/**
 * Delegated user administration — the shapes the API answers with and the
 * words the People page and Settings use for them.
 *
 * An administrator makes a staff member a PEOPLE ADMINISTRATOR and chooses the
 * roles they may grant. Every rule is enforced by the API's one shared guard
 * (`packages/api/src/services/user/user-admin-delegations/delegation-guard.ts`);
 * nothing here decides anything. The People page offers a person only the
 * actions the API's `delegated-people` list says the guard would accept, and
 * shows the API's own words when it refuses.
 */

export type Tier = "viewer" | "operator" | "admin";

/** One allowed role on a delegation (`user-admin-delegations`). */
export interface DelegationRole {
  id: string;
  /** Null when no live role has this id any more. */
  name: string | null;
  tier: Tier | null;
  /** The delegate may grant it right now. */
  grantable: boolean;
}

export interface AssignableClient {
  clientId: string;
  clientName: string | null;
  clientCode: string | null;
}

/** A delegation as the API returns it. */
export interface UserAdminDelegation {
  id: number;
  userId: number;
  userName: string | null;
  userEmail: string | null;
  delegateTier: Tier | null;
  allowedRoleIds: string[];
  allowedRoles: DelegationRole[];
  assignableClients: AssignableClient[];
  grantedBy: number;
  grantedByName: string | null;
  grantedAt: string | null;
  updatedAt: string | null;
  status: "active" | "lapsed";
  lapse: string | null;
  lapseMessage: string | null;
  /** Whether this delegate may add people now: in force, and this HQ has Microsoft sign-in (the API decides). */
  createsAccounts: boolean;
}

/** The actions the guard knows (the API's DELEGATED_ACTIONS). */
export type DelegatedAction =
  | "create-account"
  | "approve-account"
  | "reactivate-account"
  | "deactivate-account"
  | "change-role"
  | "unlock-sign-in"
  | "assign-client"
  | "unassign-client"
  | "confirm-client"
  | "unconfirm-client";

/** A person a delegate administers (`delegated-people`), with what they may do. */
export interface DelegatedPerson {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  role: string | null;
  tier: Tier;
  active: boolean;
  awaitingApprovalSince: string | null;
  lockedUntil: string | null;
  failedLoginAttempts: number;
  department: string | null;
  actions: DelegatedAction[] | string[];
}

/** One row of the append-only record (`user-admin-delegation-log`). */
export interface DelegationLogEntry {
  id: number;
  delegationId: number | null;
  action: DelegatedAction | string;
  actorUserId: number;
  actorName: string | null;
  onBehalfOfUserId: number | null;
  onBehalfOfName: string | null;
  targetUserId: number | null;
  targetName: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  createdAt: string | null;
}

/** How many log rows a page asks for; the page says so when that many come back. */
export const LOG_PAGE = 100;

export const personName = (p: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  id?: number | string;
}): string => {
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  return p.email || `Account ${p.id ?? ""}`.trim();
};

/** A person a Microsoft sign-up filed, still waiting for someone to decide. */
export const isWaiting = (p: Pick<DelegatedPerson, "active" | "awaitingApprovalSince">): boolean =>
  !p.active && !!p.awaitingApprovalSince;

/** Locked out of signing in (the lockout counters are set). */
export const isLocked = (p: Pick<DelegatedPerson, "lockedUntil" | "failedLoginAttempts">): boolean =>
  !!p.lockedUntil || p.failedLoginAttempts > 0;

export const offers = (p: DelegatedPerson, action: DelegatedAction): boolean =>
  (p.actions as string[]).includes(action);

const text = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const quoted = (v: unknown): string => (text(v) ? `"${String(v)}"` : "a role");

/**
 * One log row as a sentence a person reads. `viewer` is who is reading: the
 * affected person reads "your", everyone else reads the name.
 */
export function describeLogEntry(entry: DelegationLogEntry, viewerId?: number | null): string {
  const actor = entry.actorName ?? `Account ${entry.actorUserId}`;
  const isMe = viewerId != null && entry.targetUserId === viewerId;
  const whose = isMe ? "your" : `${entry.targetName ?? `account ${entry.targetUserId ?? "?"}`}'s`;
  const whom = isMe ? "you" : (entry.targetName ?? `account ${entry.targetUserId ?? "?"}`);
  const after = entry.after ?? {};
  const before = entry.before ?? {};
  const client = text(after.clientName) ?? text(before.clientName) ?? "a client";
  switch (entry.action) {
    case "create-account":
      return `${actor} created ${isMe ? "your account" : `an account for ${whom}`} with the role ${quoted(after.role)}.`;
    case "approve-account":
      return `${actor} approved ${whose} Microsoft sign-in with the role ${quoted(after.role)}.`;
    case "reactivate-account":
      return `${actor} reactivated ${whose} account with the role ${quoted(after.role)}.`;
    case "deactivate-account":
      return `${actor} deactivated ${whose} account.`;
    case "change-role":
      return `${actor} changed ${whose} role from ${quoted(before.role)} to ${quoted(after.role)}.`;
    // Not something a people administrator can do since the operator's
    // ruling of 2026-11-01; kept so a record made before it still reads.
    case "reset-password":
      return `${actor} reset ${whose} password.`;
    case "unlock-sign-in":
      return `${actor} unlocked ${whose} sign-in.`;
    case "assign-client":
      return `${actor} gave ${whom} access to ${client}.`;
    case "unassign-client":
      return `${actor} removed ${whose} access to ${client}.`;
    case "confirm-client":
      return `${actor} confirmed ${whose} access to ${client}.`;
    case "unconfirm-client":
      return `${actor} un-confirmed ${whose} access to ${client}.`;
    default:
      return `${actor} changed ${whose} account (${String(entry.action)}).`;
  }
}

/** "as people administrator for Zach Frye" — whose authority it was taken in. */
export const onBehalfOf = (entry: DelegationLogEntry): string | null =>
  entry.onBehalfOfName ? `as people administrator for ${entry.onBehalfOfName}` : null;

/** The API's sentence for a refusal (a Feathers error), or the fallback. */
export const refusalMessage = (err: unknown, fallback: string): string => {
  const message = (err as { message?: unknown } | null)?.message;
  return typeof message === "string" && message ? message : fallback;
};

/** Per-rule details a refusal carries (`data.errors`, e.g. a validation failure), as a list. */
export const refusalDetails = (err: unknown): string[] => {
  const errors = (err as { data?: { errors?: unknown } } | null)?.data?.errors;
  return Array.isArray(errors) ? errors.map((e) => String(e)) : [];
};
